import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  FiPaperclip, FiDownload, FiSend, FiClock, FiCheck,
  FiAlertCircle, FiFileText, FiHome, FiCalendar, FiShare2,
  FiAlignLeft, FiActivity,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { AnexoUpload } from '../../components/AnexoUpload';
import { useAuth } from '../../contexts/AuthContext';
import { useRevalidarAoFocar } from '../../hooks/useDadosVivos';
import {
  solicitacoesService, type SolicitacaoResumo, type EventoHistorico, type AnexoSolicitacao,
} from '../../services/solicitacoes.service';
import { formatarDataBR, formatarDataHoraBR } from '../../lib/data';

const STATUS_LABEL: Record<string, string> = {
  rascunho: 'Rascunho', aberto: 'Aberto', em_tramitacao: 'Em tramitação',
  aguardando_solicitante: 'Aguardando você', aguardando_aceite: 'Aguardando aceite',
  em_analise: 'Em análise', em_diligencia: 'Em diligência',
  deferido: 'Deferido', indeferido: 'Indeferido',
  arquivado: 'Arquivado', cancelado: 'Cancelado', concluido: 'Concluído',
};

export function SolicitacaoDetailPage() {
  // Número vem como splat ("/solicitacao/*") porque contém barra (ex.: ZEL-0002/2026).
  const numero = useParams()['*'];
  const [searchParams] = useSearchParams();
  const recemCriado = searchParams.get('criado') === '1';
  const navigate = useNavigate();
  const { cidadao } = useAuth();

  const [doc, setDoc] = useState<(SolicitacaoResumo & { descricao: string | null; estabelecimento_id: string }) | null>(null);
  const [historico, setHistorico] = useState<EventoHistorico[]>([]);
  const [anexos, setAnexos] = useState<AnexoSolicitacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [acaoErro, setAcaoErro] = useState<string | null>(null);
  const [acaoSucesso, setAcaoSucesso] = useState<string | null>(null);

  // Formulário de resposta (mensagem + anexos juntos)
  const [resposta, setResposta] = useState('');
  const [novosAnexos, setNovosAnexos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);

  // Anexos avulsos (enviados pela aba Anexos, sem precisar de mensagem)
  const [anexosAvulsos, setAnexosAvulsos] = useState<File[]>([]);
  const [enviandoAnexos, setEnviandoAnexos] = useState(false);

  // Aba ativa (vazio = usa o padrão calculado conforme o status)
  const [aba, setAba] = useState('');

  useEffect(() => {
    if (!cidadao || !numero) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cidadao, numero]);

  // Hot reload: revalida ao voltar para a aba.
  useRevalidarAoFocar(() => { void carregar(); }, { ativo: !!cidadao && !!numero });

  async function carregar() {
    if (!cidadao || !numero) return;
    setCarregando(true);
    try {
      const d = await solicitacoesService.buscarPorNumero(cidadao.id, numero);
      if (!d) {
        setErro('Solicitação não encontrada ou você não tem acesso.');
        return;
      }
      setDoc(d as never);
      const [h, a] = await Promise.all([
        solicitacoesService.listarHistorico(d.id),
        solicitacoesService.listarAnexos(d.id),
      ]);
      setHistorico(h);
      setAnexos(a);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao carregar.');
    } finally {
      setCarregando(false);
    }
  }

  // Envia mensagem + anexos juntos, num único fluxo.
  async function enviarResposta(e: FormEvent) {
    e.preventDefault();
    if (!doc || !cidadao) return;
    const temMsg = resposta.trim().length > 0;
    const temAnexos = novosAnexos.length > 0;
    if (!temMsg && !temAnexos) return;
    setEnviando(true);
    setAcaoErro(null);
    try {
      for (const arquivo of novosAnexos) {
        await solicitacoesService.uploadAnexo({
          documentoId: doc.id,
          estabelecimentoId: doc.estabelecimento_id,
          cidadaoId: cidadao.id,
          cidadaoNome: cidadao.nome,
          arquivo,
        });
      }
      if (temMsg) {
        await solicitacoesService.responder(doc.id, resposta);
      }
      setResposta('');
      setNovosAnexos([]);
      setAcaoSucesso('Resposta enviada à prefeitura.');
      await carregar();
    } catch (err) {
      setAcaoErro(err instanceof Error ? err.message : 'Falha ao enviar resposta.');
    } finally {
      setEnviando(false);
    }
  }

  // Envia anexos avulsos (sem mensagem) enquanto a solicitação não está finalizada.
  async function enviarAnexosAvulsos() {
    if (!doc || !cidadao || anexosAvulsos.length === 0) return;
    setEnviandoAnexos(true);
    setAcaoErro(null);
    setAcaoSucesso(null);
    try {
      for (const arquivo of anexosAvulsos) {
        await solicitacoesService.uploadAnexo({
          documentoId: doc.id,
          estabelecimentoId: doc.estabelecimento_id,
          cidadaoId: cidadao.id,
          cidadaoNome: cidadao.nome,
          arquivo,
        });
      }
      setAnexosAvulsos([]);
      setAcaoSucesso('Anexo(s) enviado(s) à prefeitura.');
      await carregar();
    } catch (err) {
      setAcaoErro(err instanceof Error ? err.message : 'Falha ao enviar anexos.');
    } finally {
      setEnviandoAnexos(false);
    }
  }

  async function baixarAnexo(anexoId: string, nomeOriginal: string) {
    try {
      const url = await solicitacoesService.getAnexoUrl(anexoId);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeOriginal;
      a.target = '_blank';
      a.click();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Falha ao baixar.');
    }
  }

  function copiarLinkPublico() {
    if (!doc) return;
    const url = `${window.location.origin}/protocolo/${doc.numero}/${doc.hash_publico}`;
    navigator.clipboard.writeText(url);
    setAcaoSucesso('Link público copiado para a área de transferência.');
  }

  if (carregando) {
    return (
      <PortalPage titulo="Solicitação" icone={<FiFileText size={26} />} largura="media" voltarHref="/minhas-solicitacoes" voltarLabel="Minhas solicitações">
        <div className="flex justify-center py-20"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erro || !doc) {
    return (
      <PainelResultado
        titulo="Solicitação não disponível"
        tom="aviso"
        icone={<FiAlertCircle size={32} />}
        descricao={erro ?? 'Tente novamente.'}
        acoes={
          <Button variant="outline" onClick={() => navigate('/minhas-solicitacoes')}>
            Voltar
          </Button>
        }
      />
    );
  }

  const finalizado = ['concluido', 'arquivado', 'cancelado'].includes(doc.status);
  const aguardandoVoce = doc.status === 'aguardando_solicitante';
  const ehEsic = doc.modulo_codigo === 'esic';
  const respondido = ['indeferido', 'deferido', 'concluido'].includes(doc.status);
  const podeRecorrer = ehEsic && respondido;

  // Pergunta/pedido mais recente da prefeitura: prioriza despacho/diligência; nunca a própria
  // resposta do cidadão nem trocas de status.
  const pergunta =
    historico.find((h) => h.mensagem && (h.tipo === 'despacho' || h.tipo === 'diligencia'))
    ?? historico.find((h) => h.mensagem && !['mudanca_status', 'resposta', 'anexo_adicionado'].includes(h.tipo))
    ?? null;

  const abas = [
    ...(aguardandoVoce ? [{ id: 'responder', label: 'Responder' }] : []),
    { id: 'solicitacao', label: 'Solicitação' },
    { id: 'historico', label: 'Linha do tempo' },
    { id: 'anexos', label: `Anexos (${anexos.length})` },
  ];
  const abaAtiva = aba || (aguardandoVoce ? 'responder' : 'solicitacao');

  return (
    <PortalPage
      titulo={doc.titulo}
      subtitulo={doc.assunto?.nome ?? undefined}
      icone={<FiFileText size={26} />}
      largura="media"
      voltarHref="/minhas-solicitacoes"
      voltarLabel="Minhas solicitações"
      heroExtra={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-blue-100/90">
          <span className="tabular-nums">{doc.numero}</span>
          <span className="uppercase tracking-wider">· {STATUS_LABEL[doc.status] ?? doc.status}</span>
          <span className="flex items-center gap-1">
            <FiCalendar size={11} /> Aberto em {formatarDataBR(doc.data_abertura)}
          </span>
          {doc.data_limite && (
            <span className="flex items-center gap-1">
              <FiClock size={11} /> Prazo {formatarDataBR(doc.data_limite)}
            </span>
          )}
          {doc.setor_atual?.nome && (
            <span className="flex items-center gap-1">
              <FiHome size={11} /> {doc.setor_atual.nome}
            </span>
          )}
          <button
            onClick={copiarLinkPublico}
            className="flex items-center gap-1 text-white/90 hover:text-white hover:underline"
            title="Copiar link público para acompanhamento sem login"
          >
            <FiShare2 size={11} /> Compartilhar link público
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {recemCriado && (
          <Alert variante="sucesso">
            <strong>Solicitação protocolada!</strong> Seu número é <strong>{doc.numero}</strong>.
            Você pode acompanhar tudo por aqui ou compartilhar o link público.
          </Alert>
        )}

        {acaoSucesso && <Alert variante="sucesso">{acaoSucesso}</Alert>}
        {acaoErro && <Alert variante="erro">{acaoErro}</Alert>}

        {/* Abas */}
        <div className="inline-flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
          {abas.map((a) => (
            <button
              key={a.id}
              onClick={() => setAba(a.id)}
              className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition ${
                abaAtiva === a.id ? 'bg-institutional text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* ABA: Responder (pergunta da prefeitura + resposta com anexo juntos) */}
        {abaAtiva === 'responder' && aguardandoVoce && (
          <div className="space-y-4">
            <SectionCard titulo="A prefeitura precisa de uma resposta" icone={<FiAlertCircle size={18} />}>
              {pergunta ? (
                <>
                  <div className="text-xs text-slate-500 mb-1.5">
                    {pergunta.ator_nome} · {formatarDataHoraBR(pergunta.criado_em)}
                  </div>
                  <div
                    className="prose prose-slate prose-sm max-w-none text-slate-800"
                    dangerouslySetInnerHTML={{ __html: pergunta.mensagem ?? '' }}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-700">
                  A prefeitura solicitou informações adicionais para dar continuidade à sua solicitação.
                </p>
              )}
            </SectionCard>

            <SectionCard titulo="Sua resposta" icone={<FiSend size={18} />}>
              <form onSubmit={enviarResposta} className="space-y-3">
                <textarea
                  value={resposta}
                  onChange={(e) => setResposta(e.target.value)}
                  placeholder="Escreva sua resposta para a prefeitura..."
                  rows={5}
                  className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
                />
                <div>
                  <div className="mb-1.5 flex items-center gap-1 text-xs font-medium text-slate-600">
                    <FiPaperclip size={12} /> Anexar arquivos (opcional)
                  </div>
                  <AnexoUpload arquivos={novosAnexos} onChange={setNovosAnexos} multiplo maxArquivos={5} tamanhoMaxMB={20} />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    loading={enviando}
                    disabled={resposta.trim().length === 0 && novosAnexos.length === 0}
                  >
                    <FiSend size={14} /> Enviar resposta
                  </Button>
                </div>
              </form>
            </SectionCard>
          </div>
        )}

        {/* ABA: Solicitação original */}
        {abaAtiva === 'solicitacao' && (
          <div className="space-y-5">
            <SectionCard titulo="Sua solicitação" icone={<FiAlignLeft size={18} />}>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{doc.descricao ?? '—'}</p>
            </SectionCard>

            {podeRecorrer && (
              <SectionCard className="border-amber-200 bg-amber-50" titulo="Recurso disponível" icone={<FiAlertCircle size={18} />}>
                <p className="text-sm text-amber-800 mb-3">
                  Se você discorda da resposta recebida, pode recorrer em até 4 instâncias hierárquicas
                  (Lei 12.527/2011 — LAI).
                </p>
                <Button onClick={() => window.location.assign(`/esic/${encodeURIComponent(doc.numero)}/recorrer`)}>
                  Recorrer da decisão
                </Button>
              </SectionCard>
            )}

            {finalizado && (
              <SectionCard>
                <div className="text-center py-8">
                  <FiCheck className="mx-auto text-emerald-500 mb-2" size={32} />
                  <p className="text-sm text-slate-700 font-medium">
                    Esta solicitação foi {STATUS_LABEL[doc.status]?.toLowerCase() ?? 'finalizada'}.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Você ainda pode consultar o histórico, mas não pode enviar novas informações.
                  </p>
                </div>
              </SectionCard>
            )}
          </div>
        )}

        {/* ABA: Linha do tempo */}
        {abaAtiva === 'historico' && (
          <SectionCard titulo="Linha do tempo" icone={<FiActivity size={18} />}>
            {historico.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma movimentação ainda.</p>
            ) : (
              <ul className="space-y-4">
                {historico.map((h) => (
                  <li key={h.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-institutional mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className="text-sm font-medium text-slate-900">
                          {h.titulo ?? formatarTipo(h.tipo)}
                        </div>
                        <div className="text-xs text-slate-500">{formatarDataHoraBR(h.criado_em)}</div>
                      </div>
                      <div className="text-xs text-slate-500">{h.ator_nome}</div>
                      {h.mensagem && (
                        <div
                          className="prose prose-slate prose-sm max-w-none text-slate-700 mt-1"
                          dangerouslySetInnerHTML={{ __html: h.mensagem }}
                        />
                      )}
                      {(h.setor_origem_nome || h.setor_destino_nome) && (
                        <div className="text-xs text-slate-500 mt-1">
                          {h.setor_origem_nome} → {h.setor_destino_nome}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        )}

        {/* ABA: Anexos */}
        {abaAtiva === 'anexos' && (
          <SectionCard titulo={`Anexos (${anexos.length})`} icone={<FiPaperclip size={18} />}>
            {anexos.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum anexo nesta solicitação.</p>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
                {anexos.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 p-3">
                    <FiPaperclip className="text-slate-400 flex-shrink-0" size={14} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{a.nome_original}</div>
                      <div className="text-xs text-slate-500">
                        {(a.tamanho_bytes / 1024).toFixed(1)} KB
                        {a.categoria && ` · ${a.categoria}`}
                      </div>
                    </div>
                    <button
                      onClick={() => baixarAnexo(a.id, a.nome_original)}
                      className="p-2 text-slate-500 hover:text-institutional rounded transition"
                      title="Baixar"
                    >
                      <FiDownload size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!finalizado && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="mb-2 flex items-center gap-1 text-sm font-medium text-slate-700">
                  <FiPaperclip size={14} /> Enviar novos anexos
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Você pode enviar documentos a esta solicitação enquanto ela estiver em andamento.
                </p>
                <AnexoUpload arquivos={anexosAvulsos} onChange={setAnexosAvulsos} multiplo maxArquivos={5} tamanhoMaxMB={20} />
                <div className="flex justify-end mt-3">
                  <Button
                    onClick={enviarAnexosAvulsos}
                    loading={enviandoAnexos}
                    disabled={anexosAvulsos.length === 0}
                  >
                    <FiSend size={14} /> Enviar anexos
                  </Button>
                </div>
              </div>
            )}
          </SectionCard>
        )}
      </div>
    </PortalPage>
  );
}

function formatarTipo(tipo: string): string {
  const map: Record<string, string> = {
    criacao: 'Solicitação aberta',
    encaminhamento: 'Encaminhamento',
    despacho: 'Despacho',
    resposta: 'Resposta',
    anexo_adicionado: 'Anexo adicionado',
    mudanca_status: 'Mudança de status',
    conclusao: 'Concluído',
    arquivamento: 'Arquivado',
    observacao: 'Observação',
  };
  return map[tipo] ?? tipo;
}
